import { HTMLAttributes } from 'react'

import BioLabel from '@/components/base/bio-label'
import Container from '@/components/base/container'
import FluidMask from '@/components/base/fluid-mask'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { productDetailHeroRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type ProductDetailHeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    media: MediaElement
    title: string
    subtitle: string
    paragraph: string
    show_bio_label: boolean
  }>

const ProductDetailHero = ({
  media,
  title,
  subtitle,
  paragraph,
  show_bio_label,
  ...props
}: ProductDetailHeroProps) => {
  return (
    <Motion animation={productDetailHeroRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className="items-start gap-y-spacing-lg">
              <div
                className={cn(
                  'col-span-2 md:col-span-3 lg:col-span-6',
                  'flex flex-col items-start gap-y-[2.4rem]'
                )}
              >
                {title ? (
                  <Typography variant="h2" component="h1" data-motion-title>
                    {title}
                  </Typography>
                ) : null}

                {subtitle ? (
                  <Typography
                    variant="large"
                    paragraph
                    className="pt-[1.6rem] opacity-80"
                    data-motion-subtitle
                  >
                    {subtitle}
                  </Typography>
                ) : null}

                {show_bio_label ? <BioLabel data-motion-label /> : null}

                {paragraph ? (
                  <Typography
                    variant="regular"
                    className="opacity-80"
                    data-motion-paragraph
                  >
                    {paragraph}
                  </Typography>
                ) : null}
              </div>
              <div
                className={cn(
                  'col-span-2 md:col-span-3 lg:col-span-4 lg:col-start-8',
                  'flex flex-col items-start gap-y-[2.4rem]'
                )}
              >
                <div className="relative aspect-[393/491]" data-motion-media>
                  <FluidMask amplitude={10}>
                    {(ref) => <Media ref={ref} fill {...media} />}
                  </FluidMask>
                </div>
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default ProductDetailHero
